import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer({
  mobile = false,
  closeMobileMenu
}: {
  mobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const filteredItems = menuItems.filter(item => item.label !== "Pakketten");
  if (loading) {
    return <div data-section-id="11657" className={`flex ${mobile ? "flex-col gap-6" : "gap-8"}`}>
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-5 w-24 bg-primary/10 animate-pulse rounded-full" />)}
      </div>;
  }
  if (mobile) {
    return <nav className="flex flex-col gap-8 items-center">
        {filteredItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="group flex items-center gap-4 text-2xl font-sans font-bold uppercase tracking-widest text-foreground/90 transition-all hover:text-primary" onClick={closeMobileMenu}>
            <span className="text-xs font-sans font-bold text-secondary tracking-tighter group-hover:text-primary">
              {(i + 1).toString().padStart(2, "0")}
            </span>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {filteredItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group flex items-center gap-2 text-sm font-sans font-medium uppercase tracking-widest text-muted-foreground transition-all hover:text-foreground">
                <span className="text-[10px] font-sans font-bold text-primary/60 transition-colors group-hover:text-secondary">
                  {(i + 1).toString().padStart(2, "0")}
                </span>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header id="header" className={`dark sticky top-0 left-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-background/70 backdrop-blur-xl border-b border-white/5 py-5 shadow-2xl shadow-black/20" : "bg-background py-10 border-b border-transparent"}`}>
        <div className="container mx-auto flex items-center justify-between px-4 md:px-6">
          {}
          <Link to="/HomePage" className="relative z-50 flex items-center gap-2">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1589767/img/north_websites_text_logo.png" alt="North Websites" className="h-8 w-auto object-contain brightness-110" />
          </Link>

          {}
          <div className="hidden md:block">
            <MenuProvider menu_id="44" className="header-menu">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4">
            <div className="hidden md:block">
              <Button asChild variant="ghost" className="group relative overflow-hidden border border-white/10 font-sans text-xs font-bold uppercase tracking-widest text-foreground transition-all duration-300 hover:border-transparent hover:bg-gradient-to-r hover:from-primary hover:to-secondary hover:text-white">
                <Link to="/ContactPage">
                  <span className="relative z-10">Start Project</span>
                </Link>
              </Button>
            </div>

            {}
            <Button variant="ghost" size="icon" className="relative z-50 md:hidden text-foreground hover:bg-transparent hover:text-secondary transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>

          {}
          <div className={`fixed inset-0 z-40 flex flex-col items-center justify-center bg-background/95 backdrop-blur-2xl transition-all duration-500 md:hidden ${isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--primary)_0%,transparent_70%)] opacity-10" />
            
            <MenuProvider menu_id="44" className="mobile-menu relative z-10 mb-12 w-full flex justify-center">
              <HeaderMenuConsumer mobile closeMobileMenu={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>

            <Button asChild size="lg" className="relative z-10 min-w-[220px] font-sans font-bold uppercase tracking-[0.2em] bg-gradient-to-r from-primary to-secondary text-white shadow-xl shadow-primary/20 hover:scale-105 transition-transform">
              <Link to="/ContactPage" onClick={() => setIsMobileMenuOpen(false)}>
                Start Project
              </Link>
            </Button>
          </div>
        </div>
      </header>
    </>;
}